import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Switch,
  Platform,
  ActivityIndicator,
} from 'react-native';
import * as Haptics from 'expo-haptics';
import Svg, { Path } from 'react-native-svg';
import CustomText from '../customText/CustomText';
import { MainButton } from '../buttons/MainButton';
import theme from '../../constants/theme.style.js';
import TextField from '../textField/TextField';
import {
  validateCheckbox,
  validateEmail,
  validateName,
  validateNickname,
  validatePassword,
} from '../../helpers/validations';
import AuthFooter from '../authFooter/AuthFooter';

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  stepsHeader: {
    marginBottom: 20,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  stepBack: {
    width: 40,
    height: 40,
    transform: [{ rotate: '90deg' }],
  },
  currentStep: {
    fontSize: theme.FONT_SIZE_TITLE,
  },
  incorrect: {
    textAlign: 'center',
    color: theme.RED_ERROR_COLOR,
  },
  switchContainer: {
    marginBottom: 10,
  },
  switchContainerInner: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  switch: {
    padding: 10,
    marginRight: 10,
  },
  dataLink: {
    color: theme.GREEN_WARM_COLOR,
    textDecorationLine: 'underline',
  },
  loading: {
    marginTop: 5,
    marginBottom: 10,
    padding: 2,
  },
});

const SignUpForm = ({ onRegister, callback, navigateToDataDeclaration }) => {
  const [nickname, setNickname] = useState('');
  const [nicknameError, setNicknameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [checkboxError, setCheckboxError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const refs = useRef({
    email: null,
    nickname: null,
    firstName: null,
    lastName: null,
    password: null,
    repeatPassword: null,
  });

  useEffect(() => {
    switch (step) {
      case 0:
        refs.current?.username?.focus();
        break;
      case 1:
        refs.current?.firstName?.focus();
        break;
      case 2:
        refs.current?.password?.focus();
        break;
      default:
        break;
    }
  }, [step]);

  const validate0 = () => {
    const errorEmail = validateEmail(email);
    setEmailError(errorEmail);
    const errorNickname = validateNickname(nickname);
    setNicknameError(errorNickname);

    return !errorEmail && !errorNickname;
  };

  const validate1 = () => {
    const errorFirstName = validateName(firstName);
    setFirstNameError(errorFirstName);
    const errorLastName = validateName(lastName);
    setLastNameError(errorLastName);

    return !errorFirstName && !errorLastName;
  };

  const validate2 = () => {
    const errorPassword = validatePassword(password, passwordRepeat);
    setPasswordError(errorPassword);
    const errorCheckbox = validateCheckbox(isChecked);
    setCheckboxError(errorCheckbox);

    return !errorPassword && !errorCheckbox;
  };

  const registerUser = async () => {
    setLoading(true);
    const result = await onRegister(nickname, email, firstName, lastName, password);

    if (result.error !== '') {
      switch (result.error.status.toString()) {
        case '911':
          setNicknameError(result.error.message);
          break;
        case '912':
          setEmailError(result.error.message);
          break;
        default:
          break;
      }

      setStep(0);
    } else {
      callback('Your account was created!\nYou can now login.');
    }

    setLoading(false);
  };

  const nextStep = async () => {
    let valid = false;

    switch (step) {
      case 0:
        valid = validate0();
        break;
      case 1:
        valid = validate1();
        break;
      case 2:
        valid = validate2();
        break;
      default:
        break;
    }

    if (Platform.OS !== 'web') {
      if (valid) {
        await Haptics.notificationAsync();
      } else {
        await Haptics.impactAsync();
      }
    }

    if (step < 2 && valid) {
      setStep((prevState) => prevState + 1);
    } else if (step === 2 && valid) {
      registerUser();
    }

    return valid;
  };

  return (
    <View style={styles.container}>
      <View style={styles.stepsHeader}>
        <CustomText style={styles.currentStep}>{step + 1}/3</CustomText>
        <TouchableOpacity
          onPress={() => setStep((prevState) => prevState - 1)}
          disabled={step === 0}
        >
          <Svg
            fill="none"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            style={styles.stepBack}
          >
            <Path
              xmlns="http://www.w3.org/2000/svg"
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.29289 9.29289C5.68342 8.90237 6.31658 8.90237 6.70711 9.29289L12 14.5858L17.2929 9.29289C17.6834 8.90237 18.3166 8.90237 18.7071 9.29289C19.0976 9.68342 19.0976 10.3166 18.7071 10.7071L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L5.29289 10.7071C4.90237 10.3166 4.90237 9.68342 5.29289 9.29289Z"
              fill={step === 0 ? theme.GREY_LIGHTER_COLOR : theme.PRIMARY_COLOR}
            />
          </Svg>
        </TouchableOpacity>
      </View>
      {step === 0 && (
        <>
          <TextField
            ref={(el) => {
              refs.current.email = el;
            }}
            label="Email"
            value={email}
            onChangeText={(value) => setEmail(value)}
            error={emailError}
            textInputId="Email"
            textContentType="emailAddress"
            keyboardType="email-address"
            returnKeyType="next"
            onSubmitEditing={() => refs.current?.nickname.focus()}
            testID="email"
          />
          <TextField
            ref={(el) => {
              refs.current.nickname = el;
            }}
            label="Username"
            value={nickname}
            onChangeText={(value) => setNickname(value)}
            autoCapitalize="sentences"
            error={nicknameError}
            textContentType="nickname"
            returnKeyType="next"
            onSubmitEditing={nextStep}
            testID="nickname"
          />
        </>
      )}

      {step === 1 && (
        <>
          <TextField
            ref={(el) => {
              refs.current.firstName = el;
            }}
            label="First Name"
            value={firstName}
            onChangeText={(value) => setFirstName(value)}
            autoCapitalize="sentences"
            error={firstNameError}
            textInputId="Firstname"
            textContentType="givenName"
            returnKeyType="next"
            onSubmitEditing={() => refs.current?.lastName.focus()}
            testID="first-name"
          />
          <TextField
            ref={(el) => {
              refs.current.lastName = el;
            }}
            label="Last Name"
            value={lastName}
            onChangeText={(value) => setLastName(value)}
            autoCapitalize="sentences"
            error={lastNameError}
            textInputId="Lastname"
            textContentType="familyName"
            returnKeyType="next"
            onSubmitEditing={nextStep}
            testID="last-name"
          />
        </>
      )}

      {step === 2 && (
        <>
          <TextField
            ref={(el) => {
              refs.current.password = el;
            }}
            label="Password"
            value={password}
            onChangeText={(value) => setPassword(value)}
            error={!!passwordError}
            textInputId="Password"
            textContentType="newPassword"
            returnKeyType="next"
            onSubmitEditing={() => refs.current?.repeatPassword.focus()}
            testID="password"
            secureTextEntry
          />
          <TextField
            ref={(el) => {
              refs.current.repeatPassword = el;
            }}
            label="Repeat Password"
            value={passwordRepeat}
            onChangeText={(value) => setPasswordRepeat(value)}
            error={passwordError}
            textContentType="newPassword"
            returnKeyType="done"
            testID="repeat-password"
            secureTextEntry
          />

          <View style={styles.switchContainer}>
            <View style={styles.switchContainerInner}>
              <View style={styles.switch}>
                <Switch
                  testID="privacy-switch"
                  trackColor={{ false: theme.GREY_LIGHT_COLOR, true: theme.GREEN_WARM_COLOR }}
                  thumbColor={theme.WHITE_COLOR}
                  ios_backgroundColor={theme.GREY_MEDIUM_LIGHT_COLOR}
                  onValueChange={setIsChecked}
                  value={isChecked}
                />
              </View>
              <CustomText>I accept the </CustomText>
              <TouchableOpacity onPress={navigateToDataDeclaration}>
                <CustomText style={styles.dataLink}>Data Policy</CustomText>
              </TouchableOpacity>
            </View>

            <CustomText style={styles.incorrect} testID="checkbox-error">
              {checkboxError}
            </CustomText>
          </View>
        </>
      )}

      {loading ? (
        <ActivityIndicator style={styles.loading} color={theme.PRIMARY_COLOR} size="large" />
      ) : (
        <MainButton
          title={step === 2 ? 'Register' : 'Next'}
          btnBgColor={theme.GREEN_DARKER_COLOR}
          textColor="#FFFFFF"
          btnTextSize={15}
          btnWidth={200}
          btnPadding="5%"
          btnMarginTop={5}
          btnMarginBottom={10}
          btnPress={nextStep}
        />
      )}

      <AuthFooter
        question="Already have an account?"
        linkTitle="Sign In here"
        onClick={() => callback('')}
        navigateToDataDeclaration={navigateToDataDeclaration}
      />
    </View>
  );
};

export default SignUpForm;
